import { mixins } from "@fastly/consistently"
import styled, { css } from "styled-components"

const { marginBlock } = mixins

/* To use as we migrate the Hero component to styled components
  Takes children (should be rich texted)
  4/21 this works for product pages in dark theme w/ text aside
*/
const themes = {
  dark: {
    h1: "var(--white)",
    color: "var(--grey-40)",
    linkColor: "var(--blue-light)",
    hoverColor: "var(--white)",
    background: "var(--color-black)",
  },
  light: {
    h1: "var(--grey-90)",
    color: "var(--grey-90)",
    linkColor: "inherit",
    hoverColor: "inherit",
    background: "var(--grey-20)",
  },
  altLight: {
    h1: "var(--grey-90)",
    color: "var(--grey-90)",
    linkColor: "inherit",
    hoverColor: "inherit",
    background: "var(--white)",
    position: "relative",
  },
}

const layouts = {
  aside: `
    text-align: left;
    display: grid;
    gap: 24px 32px;

    @media (min-width: 768px) {
      grid-template-rows: fit-content;
      grid-template-columns: 1fr 1fr;
    }

    @media (min-width: 1200px) {
      column-gap: 128px;
    }

    & p {
      max-width: 100%;
    }
  `,

  asideWithImage: `
  text-align: left;
  display: grid;
  gap: 24px 32px;

  @media (min-width: 768px) {
    grid-template-rows: fit-content;
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1200px) {
    column-gap: 0;
  }

  & p {
    max-width: 100%;
  }

  & div:nth-of-type(2){
    justify-self: end;
  }
`,
}

const eyebrows = {
  tag: `text-transform: none;
    padding: .4rem .67rem;
    width: fit-content;
    background: var(--grey-70);
    border-radius: 4px;
    font: var(--eyebrow);

    @media (min-width: 768px) {
      grid-column: 1 /-1;
    }`,
  default: `text-transform: uppercase;`,
}

const StyledHeroWrapper = styled.div`
  background: ${(props) => themes[props.theme]?.background || `var(--grey-20)`};
  color: ${(props) => themes[props.theme]?.color || `var(--grey-90)`};
  text-align: center;
  padding: 4.2666666667rem 0 6.4rem;
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: ${(props) => themes[props.theme]?.position || `auto`};

  ${(props) =>
    props.id &&
    css`
      overflow: hidden;
      position: relative;
      @media (min-width: 1200px) {
        &::after {
          content: "";
          width: calc(100% - 60vw);
          height: 388px;
          background: url("/g-static/nimbus/hero${props.id}_bg_graphic.svg") no-repeat;
          background-position: -50% 50%;
          position: absolute;
          bottom: 0;
          right: 0;
        }
      }
    `};
`

const StyledHeroContent = styled.header`
  margin: 0 16px;
  max-width: ${(props) => props.addStyles?.maxWidth || `1140px`};
  ${(props) => layouts[props.layout] || ``}

  @media (min-width: 1200px) {
    margin: 0 auto;
  }

  & h1 {
    color: ${(props) => themes[props.theme]?.h1 || `var(--grey-90)`};
    font-size: var(--font-size-v3-4xl);
  }

  & div p:first-child,
  & div p {
    color: ${(props) => themes[props.theme]?.color || `var(--grey-90)`};
    max-width: 550px;
    ${marginBlock(0, "1rem")}
    font-weight: normal;
    padding: 0;
    background: inherit;
    letter-spacing: -0.22px;
  }

  & div > a + a {
    margin-left: 20px;
  }

  & p:first-child,
  & div:first-of-type > p:first-child {
    ${(props) => eyebrows[props.addStyles?.eyebrowType]};
    font-weight: 700;
    color: ${(props) => props.addStyles?.tagColor || `var(--grey-60)`};
    background: ${(props) => props.addStyles?.tagBackground};
    margin-bottom: 1rem;
  }

  & p:empty {
    display: none;
  }

  & p > a {
    color: ${(props) => themes[props.theme]?.linkColor || `var(--color-link)`};
    text-decoration: none;
    cursor: pointer;
  }

  & p > a:hover {
    text-decoration: underline;
    color: ${(props) => themes[props.theme]?.hoverColor || `var(--color-link-hover)`};
  }

  ${(props) =>
    props.addStyles?.linkColor &&
    css`
      & a {
        color: ${props.addStyles.linkColor};
      }
    `}

  ${(props) =>
    props.addStyles?.inlineAnchor
      ? css`
          & a {
            display: inline;
          }

          & a:after {
            content: none;
          }
        `
      : css`
          & div:first-child a {
            display: inline-block;
            margin-block-start: 1rem;
          }
        `}

  .security-img {
    position: relative;
    z-index: 1;

    &::after {
      content: "";
      position: absolute;
      width: 379px;
      height: 229px;
      border-radius: 1rem;
      background-color: var(--grey-80);
      opacity: 0.7;
      bottom: 0;
      z-index: -1;
      transform: translate(-54%, 50%);

      @media (min-width: 768px) {
        transform: translate(-70%, -30%);
      }

      @media (min-width: 1200px) {
        transform: translate(-65%, 30%);
        width: 569px;
        height: 333px;
      }
    }
  }
  .deliver-img {
    position: relative;
    z-index: 1;
    min-height: 200px;

    img {
      position: absolute;
      right: -10%;
      top: 15%;
      transform: scale(1.2);
    }

    &::after {
      background: url(/g-static/nimbus/deliver-heroNimbus.svg);
      content: "";
      position: absolute;
      width: 924px;
      height: 592px;
      top: 48px;
      left: 48px;
      z-index: -1;
    }
  }
`

const StyledHero = ({ children, theme, layout, id, addStyles, className }) => {
  return (
    <StyledHeroWrapper theme={theme} layout={layout} id={id} className={className}>
      <StyledHeroContent theme={theme} layout={layout} addStyles={addStyles}>
        {children}
      </StyledHeroContent>
    </StyledHeroWrapper>
  )
}

export default StyledHero
